import _ from "lodash";
import React, {
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
  useEffect,
} from "react";
import moment from "moment";
import classNames from "classnames";

import { EmptyState } from "@toddle-edu/ds-web";
import { NoMessagesIllustration } from "@toddle-edu/ds-theme";

import { useHandleScroll } from "Utils/CustomHooks";

import { graphql } from "lib/apolloClient";
import { getOrganizationId } from "modules/CommonSelectors";
import prepareLoadMoreFeed from "Utils/PrepareLoadMoreFeedHOF";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import {
  PAGINATION_LIMITS,
  MESSAGE_TYPE,
  CHAT_TYPE_BE,
  CHAT_TYPE,
  getMessageDOMId,
} from "MessagingV2/modules/Utils";
import { useI18n } from "Hooks";
import Messages from "MessagingV2/components/Messages";
import { getChatMessagesForPreviewQuery } from "MessagingV2/modules/MessagingQuery";

import classes from "./ConversationsPreview.scss";

const MessagesPreviewTopToBottom = props => {
  const {
    type,
    lastReadMessage,
    networkStatus,
    hasNextPage,
    messagesEdges,
    loadMoreMessages,
  } = props;
  const containerRef = useRef();
  const { t } = useI18n(["common"]);

  const chatType =
    type === CHAT_TYPE_BE.CHAT_PERSONAL ? CHAT_TYPE.CHAT : CHAT_TYPE.CHANNEL;
  const isLoadingMore = networkStatus === 3;

  useHandleScroll({
    hasNextPage,
    networkStatus,
    loadMoreFeed: loadMoreMessages,
    getScrollRef: () => containerRef.current,
  });

  const getMessageElementIdCb = useCallback(message => {
    const id = getMessageDOMId(message);
    return `Preview-modal-${id}`;
  }, []);

  if (_.isEmpty(messagesEdges)) {
    return (
      <div className={classes.emptyContainer}>
        <div className={classes.emptyInnerContainer}>
          <EmptyState
            size="small"
            illustration={NoMessagesIllustration}
            subtitle={t("common:no_conversation_summary_included")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.scrollableContainer} ref={containerRef}>
      <div className={classes.emptySpace} />
      <Messages
        chatType={chatType}
        messages={messagesEdges}
        lastReadMessage={lastReadMessage}
        onEditMessageClick={_.noop}
        onRetryMessageClick={_.noop}
        onReplyMessageClick={_.noop}
        onParentMessageClick={_.noop}
        highlightedMessageId={null}
        isMessageOptionsVisible={false}
        unreadMessageCount={0}
        showUnreadLine={false}
        getMessageElementIdCb={getMessageElementIdCb}
      />
      {isLoadingMore ? (
        <div className={classes.loaderContainer}>
          <Loading />
        </div>
      ) : null}
    </div>
  );
};

const MessagesPreviewBottomToTop = props => {
  const {
    type,
    lastReadMessage,
    networkStatus,
    hasNextPage,
    messagesEdges,
    loadMoreMessages,
    chatId,
  } = props;
  const containerRef = useRef();
  const lastScrollPosition = useRef({
    scrollHeight: 0,
  });
  const { t } = useI18n(["common"]);

  const chatType =
    type === CHAT_TYPE_BE.CHAT_PERSONAL ? CHAT_TYPE.CHAT : CHAT_TYPE.CHANNEL;
  const isLoadingMore = networkStatus === 3;

  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [chatId]);

  const reversedConversations = useMemo(
    () => _.reverse(_.clone(messagesEdges)),
    [messagesEdges]
  );

  useEffect(() => {
    const containerDiv = containerRef.current;
    messagesEdges;
    if (lastScrollPosition.current.scrollHeight) {
      if (!containerDiv) return;
      containerDiv.scrollTop =
        containerDiv.scrollHeight - lastScrollPosition.current.scrollHeight;
      lastScrollPosition.current.scrollHeight = 0;
    }
  }, [messagesEdges]);

  const handleLoadMoreMessages = async () => {
    const containerDiv = containerRef.current;
    lastScrollPosition.current.scrollHeight = containerDiv?.scrollHeight;

    await loadMoreMessages();
  };

  useHandleScroll({
    networkStatus,
    loadMoreFeed: handleLoadMoreMessages,
    hasNextPage,
    getScrollRef: () => containerRef.current,
    isScrollAtBottom: false,
  });

  const getMessageElementIdCb = useCallback(message => {
    const id = getMessageDOMId(message);
    return `Preview-modal-${id}`;
  }, []);

  if (_.isEmpty(messagesEdges)) {
    return (
      <div className={classes.emptyContainer}>
        <div className={classes.emptyInnerContainer}>
          <EmptyState
            size="small"
            illustration={NoMessagesIllustration}
            subtitle={t(
              "common:no_messages_to_summarise_in_the_selected_range"
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.scrollableContainer} ref={containerRef}>
      <div className={classes.emptySpace} />
      {isLoadingMore ? (
        <div className={classes.loaderContainer}>
          <Loading />
        </div>
      ) : null}
      <Messages
        chatType={chatType}
        messages={reversedConversations}
        lastReadMessage={lastReadMessage}
        onEditMessageClick={_.noop}
        onRetryMessageClick={_.noop}
        onReplyMessageClick={_.noop}
        onParentMessageClick={_.noop}
        highlightedMessageId={null}
        isMessageOptionsVisible={false}
        unreadMessageCount={0}
        showUnreadLine={false}
        getMessageElementIdCb={getMessageElementIdCb}
      />
    </div>
  );
};

const MessagesPreviewTopToBottomWithLoader = withLoader(
  MessagesPreviewTopToBottom
);

const MessagesPreviewBottomToTopWithLoader = withLoader(
  MessagesPreviewBottomToTop
);

const ConversationsPreview = props => {
  const {
    selectedDuration,
    type,
    lastReadMessage,
    networkStatus,
    hasNextPage,
    messagesEdges,
    loadMoreMessages,
    isLoading = false,
    isData = true,
    showFromLatest,
    chatId,
    headerText,
  } = props;

  const { t } = useI18n(["common"]);

  return (
    <div className={classes.previewContentContainer}>
      <div className={classNames("text-label-3", classes.previewText)}>
        {headerText ? headerText : t("common:preview")}
      </div>
      <div className={classes.messagesContainer}>
        {showFromLatest ? (
          <MessagesPreviewBottomToTopWithLoader
            duration={selectedDuration}
            type={type}
            chatId={chatId}
            lastReadMessage={lastReadMessage}
            networkStatus={networkStatus}
            hasNextPage={hasNextPage}
            messagesEdges={messagesEdges}
            loadMoreMessages={loadMoreMessages}
            isLoading={isLoading}
            isData={isData}
          />
        ) : (
          <MessagesPreviewTopToBottomWithLoader
            duration={selectedDuration}
            type={type}
            lastReadMessage={lastReadMessage}
            networkStatus={networkStatus}
            hasNextPage={hasNextPage}
            messagesEdges={messagesEdges}
            loadMoreMessages={loadMoreMessages}
            isLoading={isLoading}
            isData={isData}
          />
        )}
      </div>
    </div>
  );
};

export const getMessagesMemoize = _.memoize(messagesEdges =>
  _.reject(messagesEdges, ({ node }) => node.isDeleted)
);

export default graphql(getChatMessagesForPreviewQuery, {
  name: "getChatMessagesForPreviewQuery",
  skip: ({ selectedDate, showFromLatest }) => !selectedDate && !showFromLatest,
  options: ({ selectedDate, chatId, showFromLatest }) => {
    const after = moment(selectedDate).startOf("day").toISOString(true);
    return {
      fetchPolicy: "cache-and-network",
      variables: {
        id: chatId,
        organizationId: getOrganizationId(),
        input: {
          first: PAGINATION_LIMITS.MESSAGES_FEED,
          messageType: [MESSAGE_TYPE.NORMAL],
          orderByDirection: "ASC",
          ...(showFromLatest
            ? {
                orderByDirection: "DESC",
              }
            : {
                dateFrom: after, // only dateFrom is needed as beginning point
                orderByDirection: "ASC",
              }),
        },
      },
    };
  },
  props: ({ getChatMessagesForPreviewQuery }) => {
    const { networkStatus, variables, node } = getChatMessagesForPreviewQuery;
    const { type, lastReadMessage } = node ?? {};
    const messagesEdges = getMessagesMemoize(
      _.get(node, "messagesV2.edges", [])
    );

    const { hasNextPage, endCursor } = _.get(node, "messagesV2.pageInfo", {});
    const isLoading = _.includes([1, 2], networkStatus);

    // Basically show loader every time in cache-and-network if
    // the list is empty, to avoid flickering(from empty state to date state)
    const isData =
      !_.isEmpty(node) && (!_.isEmpty(messagesEdges) || !isLoading);

    return {
      isLoading,
      isData,
      type,
      lastReadMessage,
      messagesEdges,
      hasNextPage,
      networkStatus,
      loadMoreMessages: prepareLoadMoreFeed({
        variables: {
          ...variables,
          input: { ...variables.input, before: endCursor },
        },
        queryData: getChatMessagesForPreviewQuery,
        key: "messagesV2",
      }),
    };
  },
})(ConversationsPreview);
